import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Tab,
  Tabs,
} from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import NotificationsContext from "../../../contexts/NotificationsContext";
import {
  getTrainingCategories,
  createTrainingCategory,
  updateTrainingCategory,
  deleteTrainingCategory,
  getTrainingVideos,
  createTrainingVideo,
  updateTrainingVideo,
  deleteTrainingVideo,
  uploadLocalVideo,
  createVideoLink,
  deleteVideoLink,
} from "../../../services/TrainingService";
import TrainingAccessControl from "./TrainingAccessControl";

const TrainingManagement = () => {
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingVideo, setSubmittingVideo] = useState(false);

  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);

  // Form data
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    order: 1,
    prerequisite: "",
  });

  const [videoForm, setVideoForm] = useState({
    title: "",
    description: "",
    video_type: "external",
    external_url: "",
    s3_url: "",
    category: "",
    order: 1,
    duration: "",
    thumbnail_url: "",
    thumbnail_final_url: "",
    is_active: true,
  });

  // Estado para upload de thumbnail
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(null);

  // Estado para upload de vídeo
  const [videoFile, setVideoFile] = useState(null);
  const [videoFileError, setVideoFileError] = useState(null);
  
  // Estados para upload local (NOVO)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [useLocalUpload, setUseLocalUpload] = useState(false);

  // Estado para materiais auxiliares (links)
  const [videoLinks, setVideoLinks] = useState([]);
  const [newLink, setNewLink] = useState({ title: "", description: "", url: "" });

  // Estado separado para o campo de duração
  const [durationInput, setDurationInput] = useState("00:00:00");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesData, videosData] = await Promise.all([
        getTrainingCategories(),
        getTrainingVideos(),
      ]);
      setCategories(categoriesData);
      setVideos(videosData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao carregar dados",
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  }, [setNotify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateTrainingCategory(editingCategory.id, categoryForm);
        setNotify({
          title: "Sucesso!",
          message: "Categoria atualizada com sucesso!",
          type: "success",
          visible: true,
        });
      } else {
        await createTrainingCategory(categoryForm);
        setNotify({
          title: "Sucesso!",
          message: "Categoria criada com sucesso!",
          type: "success",
          visible: true,
        });
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      loadData();
    } catch (error) {
      console.error("Erro ao salvar categoria:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao salvar categoria",
        type: "error",
        visible: true,
      });
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setSubmittingVideo(true);

    // Capturar link pendente que o usuário preencheu mas não clicou em "Adicionar"
    let currentLinks = [...videoLinks];
    if (newLink.title.trim() && newLink.url.trim()) {
      currentLinks.push({
        ...newLink,
        id: `temp_${Date.now()}`,
        order: currentLinks.length,
      });
      setVideoLinks(currentLinks);
      setNewLink({ title: "", description: "", url: "" });
    }

    try {
      // Validações específicas
      if (videoForm.video_type === "external" && !videoForm.external_url) {
        throw new Error("URL do vídeo é obrigatória para vídeos externos");
      }

      if (
        videoForm.video_type === "internal" &&
        !videoFile &&
        !videoForm.s3_url
      ) {
        throw new Error("Arquivo de vídeo é obrigatório para vídeos internos");
      }

      if (videoForm.video_type === "internal" && !user?.is_staff) {
        throw new Error("Apenas usuários staff podem fazer upload de vídeos");
      }
      
      // Validações para upload local (NOVO)
      if (useLocalUpload) {
        if (!user?.is_staff) {
          throw new Error("Upload local disponível apenas para administradores");
        }
        if (!videoFile) {
          throw new Error("Arquivo de vídeo é obrigatório para upload local");
        }
        if (!videoForm.title) {
          throw new Error("Título é obrigatório para upload local");
        }
        if (!videoForm.category) {
          throw new Error("Categoria é obrigatória para upload local");
        }
      }

      console.log("Submitting video form:", videoForm);
      console.log("Duration input:", durationInput);
      console.log("Thumbnail file:", thumbnailFile);
      console.log("Video file:", videoFile);
      console.log("Editing video:", editingVideo);

      // Converter duração para formato da API
      const durationForAPI = formatDurationForAPI(durationInput);
      console.log("Duration for API:", durationForAPI);
      
      // NOVO: Verificar se deve usar upload local
      if (useLocalUpload && videoFile && user?.is_staff) {
        console.log("📏 Usando upload local para usuário staff");
        
        // Criar FormData para upload local
        const localUploadData = new FormData();
        localUploadData.append("video_file", videoFile);
        localUploadData.append("title", videoForm.title);
        localUploadData.append("category_id", videoForm.category);
        
        if (videoForm.description) {
          localUploadData.append("description", videoForm.description);
        }
        
        if (durationForAPI) {
          // Converter para segundos se necessário
          const durationSeconds = convertDurationToSeconds(durationForAPI);
          if (durationSeconds > 0) {
            localUploadData.append("duration_seconds", durationSeconds);
          }
        }
        
        localUploadData.append("order", videoForm.order);
        
        try {
          setIsUploading(true);
          setUploadProgress(0);
          
          const result = await uploadLocalVideo(localUploadData, (progressData) => {
            setUploadProgress(progressData.progress);
          });
          
          // Salvar materiais auxiliares para o vídeo local recém-criado
          if (currentLinks.length > 0 && result.video?.id) {
            await saveVideoLinks(result.video.id, currentLinks);
          }

          setNotify({
            title: "Sucesso!",
            message: `Vídeo "${result.video.title}" foi salvo localmente com sucesso!`,
            type: "success",
            visible: true,
          });

          setShowVideoModal(false);
          resetVideoForm();
          loadData();
          return;
          
        } catch (error) {
          console.error("❌ Erro no upload local:", error);
          
          let errorMessage = "Erro ao fazer upload local do vídeo";
          
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message.includes("413")) {
            errorMessage = "Arquivo muito grande. Máximo permitido: 500MB";
          } else if (error.message.includes("403")) {
            errorMessage = "Permissão negada. Upload local requer permissão de administrador";
          }
          
          setNotify({
            title: "Erro no Upload!",
            message: errorMessage,
            type: "error",
            visible: true,
          });
          
          throw error;
        } finally {
          setIsUploading(false);
          setUploadProgress(0);
        }
      }
      
      // Lógica original para uploads tradicionais
      let payload;

      // Se há arquivo de thumbnail ou vídeo para upload, usar FormData
      if (thumbnailFile || videoFile) {
        payload = new FormData();
        payload.append("title", videoForm.title);
        payload.append("description", videoForm.description);
        payload.append("video_type", videoForm.video_type);
        if (videoForm.video_type === "external") {
          payload.append("external_url", videoForm.external_url);
        } else {
          payload.append("s3_url", videoForm.s3_url);
        }
        payload.append("category", videoForm.category);
        payload.append("order", videoForm.order);
        payload.append("duration", durationForAPI);
        payload.append("is_active", videoForm.is_active);

        if (thumbnailFile) payload.append("thumbnail", thumbnailFile);

        // Adicionar video_file se houver upload de vídeo
        if (videoFile) {
          payload.append("video_file", videoFile);
        }

        payload.append("content_type", "local");

        // Não enviar thumbnail_url se temos arquivo de upload
      } else {
        // Usar JSON normal se não há upload de arquivo
        payload = {
          ...videoForm,
          duration: durationForAPI,
          content_type: "local",
        };

        // Para tipo internal sem upload, garantir que s3_url seja enviado
        if (videoForm.video_type === "internal" && !videoForm.s3_url) {
          payload.external_url = "";
        }
      }

      if (editingVideo) {
        await updateTrainingVideo(editingVideo.id, payload);
        // Salvar links novos para o vídeo editado
        if (currentLinks.some((l) => String(l.id).startsWith("temp_"))) {
          await saveVideoLinks(editingVideo.id, currentLinks);
        }
        // Deletar links removidos pelo usuário
        const originalLinks = editingVideo.links || [];
        const removedLinks = originalLinks.filter(
          (ol) => !currentLinks.some((vl) => vl.id === ol.id)
        );
        for (const rl of removedLinks) {
          try { await deleteVideoLink(editingVideo.id, rl.id); } catch (e) {}
        }
        setNotify({
          title: "Sucesso!",
          message: "Vídeo atualizado com sucesso!",
          type: "success",
          visible: true,
        });
      } else {
        const createdVideo = await createTrainingVideo(payload);
        // Salvar links para o vídeo recém-criado
        if (currentLinks.length > 0 && createdVideo?.id) {
          await saveVideoLinks(createdVideo.id, currentLinks);
        }
        setNotify({
          title: "Sucesso!",
          message: "Vídeo criado com sucesso!",
          type: "success",
          visible: true,
        });
      }
      setShowVideoModal(false);
      resetVideoForm();
      loadData();
    } catch (error) {
      console.error("Erro ao salvar vídeo:", error);
      setNotify({
        title: "Erro!",
        message: `Erro ao salvar vídeo: ${
          error.response?.data?.message || error.message
        }`,
        type: "error",
        visible: true,
      });
    } finally {
      setSubmittingVideo(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Tem certeza que deseja deletar esta categoria?")) {
      try {
        await deleteTrainingCategory(categoryId);
        setNotify({
          title: "Sucesso!",
          message: "Categoria deletada com sucesso!",
          type: "success",
          visible: true,
        });
        loadData();
      } catch (error) {
        console.error("Erro ao deletar categoria:", error);
        setNotify({
          title: "Erro!",
          message: "Erro ao deletar categoria",
          type: "error",
          visible: true,
        });
      }
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm("Tem certeza que deseja deletar este vídeo?")) {
      try {
        await deleteTrainingVideo(videoId);
        setNotify({
          title: "Sucesso!",
          message: "Vídeo deletado com sucesso!",
          type: "success",
          visible: true,
        });
        loadData();
      } catch (error) {
        console.error("Erro ao deletar vídeo:", error);
        setNotify({
          title: "Erro!",
          message: "Erro ao deletar vídeo",
          type: "error",
          visible: true,
        });
      }
    }
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({
        name: category.name,
        description: category.description || "",
        order: category.order,
        prerequisite: category.prerequisite || "",
      });
      setEditingCategory(category);
    } else {
      resetCategoryForm();
    }
    setShowCategoryModal(true);
  };

  const openVideoModal = async (video = null) => {
    if (video) {
      console.log("Editing video data:", video);
      console.log("Available video fields:", Object.keys(video));

      let videoData = video;

      // Se os dados estão incompletos, buscar dados detalhados
      if (!video.description || !video.external_url) {
        try {
          console.log("Fetching detailed video data for:", video.id);
          const { getTrainingVideo } = await import(
            "../../../services/TrainingService"
          );
          videoData = await getTrainingVideo(video.id);
          console.log("Detailed video data:", videoData);
        } catch (error) {
          console.warn("Failed to fetch detailed video data:", error);
          videoData = video; // Usar dados da listagem mesmo que incompletos
        }
      }

      // Mapear campos baseado na estrutura real da API
      const categoryValue = videoData.category || videoData.category_id || "";

      const formData = {
        title: videoData.title || "",
        description: videoData.description || "",
        video_type: videoData.video_type || "external",
        external_url: videoData.external_url || "",
        s3_url: videoData.s3_url || "",
        category: String(categoryValue),
        order: parseInt(videoData.order) || 1,
        duration: videoData.duration || "",
        thumbnail_url: videoData.thumbnail_url || "",
        thumbnail_final_url: videoData.thumbnail_final_url || "",
        is_active:
          videoData.is_active !== undefined ? videoData.is_active : true,
      };

      console.log("Final form data:", formData);
      setVideoForm(formData);
      setDurationInput(formatDurationForInput(formData.duration));
      setEditingVideo(videoData);

      // Limpar estados de thumbnail e vídeo ao editar
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setThumbnailError(null);
      setVideoFile(null);
      setVideoFileError(null);

      // Carregar materiais auxiliares existentes
      setVideoLinks(videoData.links || []);
      setNewLink({ title: "", description: "", url: "" });
    } else {
      resetVideoForm();
      setEditingVideo(null);
    }
    setShowVideoModal(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      description: "",
      order: 1,
      prerequisite: "",
    });
    setEditingCategory(null);
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: "",
      description: "",
      video_type: "external",
      external_url: "",
      s3_url: "",
      category: "",
      order: 1,
      duration: "",
      thumbnail_url: "",
      thumbnail_final_url: "",
      is_active: true,
    });
    setDurationInput("00:00:00");
    setEditingVideo(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
    setVideoFile(null);
    setVideoFileError(null);
    
    // Reset estados do upload local
    setUseLocalUpload(false);
    setUploadProgress(0);
    setIsUploading(false);

    // Reset materiais auxiliares
    setVideoLinks([]);
    setNewLink({ title: "", description: "", url: "" });
  };

  // Handlers para materiais auxiliares (links)
  const handleAddLink = () => {
    if (!newLink.title.trim() || !newLink.url.trim()) return;
    setVideoLinks((prev) => [...prev, { ...newLink, id: `temp_${Date.now()}`, order: prev.length }]);
    setNewLink({ title: "", description: "", url: "" });
  };

  const handleRemoveLink = (linkId) => {
    setVideoLinks((prev) => prev.filter((l) => l.id !== linkId));
  };

  const saveVideoLinks = async (videoId, linksToSave) => {
    for (const link of linksToSave) {
      if (String(link.id).startsWith("temp_")) {
        await createVideoLink(videoId, {
          title: link.title,
          description: link.description || "",
          url: link.url,
          order: link.order || 0,
        });
      }
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    setThumbnailError(null);

    if (!file) {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }

    // Validação do tipo de arquivo
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setThumbnailError("Formato não suportado. Use JPG, PNG ou WebP.");
      return;
    }

    // Validação do tamanho (1MB = 1024 * 1024 bytes)
    if (file.size > 1024 * 1024) {
      setThumbnailError("Arquivo muito grande. Máximo 1MB.");
      return;
    }

    setThumbnailFile(file);

    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setThumbnailPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
    // Limpar também o input
    const fileInput = document.querySelector(
      'input[type="file"][accept="image/*"]'
    );
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    setVideoFileError(null);

    if (!file) {
      setVideoFile(null);
      return;
    }

    // Validação do tipo de arquivo
    const allowedTypes = [
      "video/mp4",
      "video/mov",
      "video/quicktime",
      "video/avi",
      "video/x-msvideo",
      "video/x-matroska",
      "video/webm",
    ];
    if (!allowedTypes.includes(file.type)) {
      setVideoFileError(
        "Formato não suportado. Use MP4, MOV, AVI, MKV ou WebM."
      );
      return;
    }

    // Validação do tamanho (500MB = 500 * 1024 * 1024 bytes)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      setVideoFileError("Arquivo muito grande. Máximo 500MB.");
      return;
    }

    setVideoFile(file);
    
    // NOVO: Sugerir upload local para usuários staff
    if (user?.is_staff && !useLocalUpload) {
      setUseLocalUpload(true);
      console.log("📏 Usuário staff detectado - sugerindo upload local");
    }
    
    // Automaticamente mudar para tipo 'internal' quando selecionar arquivo
    setVideoForm((prev) => ({ ...prev, video_type: "internal" }));
  };

  const removeVideoFile = () => {
    setVideoFile(null);
    setVideoFileError(null);
    // Limpar também o input
    const fileInput = document.querySelector(
      'input[type="file"][accept="video/*"]'
    );
    if (fileInput) {
      fileInput.value = "";
    }
    // Voltar para tipo 'external' se não há arquivo
    setVideoForm((prev) => ({ ...prev, video_type: "external" }));
  };

  // Funções auxiliares para campo duration (string)
  const formatDurationForInput = (durationString) => {
    if (!durationString) return "00:00:00";

    // Se já está no formato correto, retornar
    if (durationString.includes(":")) {
      // Garantir formato HH:MM:SS para input type="time"
      const parts = durationString.split(":");
      if (parts.length === 2) {
        // MM:SS -> 00:MM:SS
        return `00:${durationString}`;
      } else if (parts.length === 3) {
        // HH:MM:SS -> manter
        return durationString;
      }
    }

    return "00:00:00";
  };

  const formatDurationForAPI = (timeInputValue) => {
    if (!timeInputValue) return "";

    // Input type="time" retorna HH:MM:SS
    // Podemos enviar direto para API ou simplificar MM:SS se horas = 00
    const [hours, minutes, seconds] = timeInputValue.split(":");

    if (hours === "00") {
      // Se não tem horas, enviar como MM:SS
      return `${minutes}:${seconds}`;
    } else {
      // Se tem horas, enviar como HH:MM:SS
      return timeInputValue;
    }
  };
  
  // NOVO: Converter duração para segundos (para upload local)
  const convertDurationToSeconds = (timeString) => {
    if (!timeString) return 0;
    
    const parts = timeString.split(":");
    
    if (parts.length === 2) {
      // MM:SS
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    } else if (parts.length === 3) {
      // HH:MM:SS
      return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    }
    
    return 0;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando dados...</p>
        </div>
      </Container>
    );
  }

  return (
    <TrainingAccessControl requireManagement={true}>
      <Container className="mt-4">
        <div className="page-header mb-4">
          <h1>Gerenciar Treinamentos</h1>
          <p className="text-muted">
            Gerencie categorias e vídeos de treinamento
          </p>
        </div>

        <Tabs defaultActiveKey="categories" className="mb-4">
          {/* Aba Categorias */}
          <Tab eventKey="categories" title="Categorias">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Categorias de Treinamento</h5>
              <Button variant="primary" onClick={() => openCategoryModal()}>
                <i className="las la-plus"></i> Nova Categoria
              </Button>
            </div>

            <Card>
              <Card.Body>
                {categories.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Ordem</th>
                          <th>Nome</th>
                          <th>Descrição</th>
                          <th>Vídeos</th>
                          <th>Pré-requisito</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(categories) &&
                          categories.map((category) => (
                            <tr key={category.id}>
                              <td>{category.order}</td>
                              <td>{category.name}</td>
                              <td>{category.description || "-"}</td>
                              <td>
                                <Badge bg="info">
                                  {category.videos_count} vídeos
                                </Badge>
                              </td>
                              <td>{category.prerequisite_name || "Nenhum"}</td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openCategoryModal(category)}
                                >
                                  <i className="las la-edit"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteCategory(category.id)
                                  }
                                >
                                  <i className="las la-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i
                      className="las la-folder-open"
                      style={{ fontSize: "48px" }}
                    ></i>
                    <p>Nenhuma categoria criada</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Aba Vídeos */}
          <Tab eventKey="videos" title="Vídeos">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Vídeos de Treinamento</h5>
              <Button variant="primary" onClick={() => openVideoModal()}>
                <i className="las la-plus"></i> Novo Vídeo
              </Button>
            </div>

            <Card>
              <Card.Body>
                {videos.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Ordem</th>
                          <th>Título</th>
                          <th>Categoria</th>
                          <th>Tipo</th>
                          <th>Duração</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(videos) &&
                          videos.map((video) => (
                            <tr key={video.id}>
                              <td>{video.order}</td>
                              <td>{video.title}</td>
                              <td>{video.category_name}</td>
                              <td>
                                <Badge
                                  bg={
                                    video.video_type === "external"
                                      ? "primary"
                                      : video.video_type === "local"
                                      ? "success"
                                      : "secondary"
                                  }
                                >
                                  {video.video_type === "external"
                                    ? "Externo"
                                    : video.video_type === "local"
                                    ? "Local"
                                    : "Upload"}
                                </Badge>
                              </td>
                              <td>{video.duration_display || "-"}</td>
                              <td>
                                <Badge
                                  bg={video.is_active ? "success" : "secondary"}
                                >
                                  {video.is_active ? "Ativo" : "Inativo"}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openVideoModal(video)}
                                >
                                  <i className="las la-edit"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteVideo(video.id)}
                                >
                                  <i className="las la-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i
                      className="las la-video"
                      style={{ fontSize: "48px" }}
                    ></i>
                    <p>Nenhum vídeo criado</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Modal Categoria */}
        <Modal
          show={showCategoryModal}
          onHide={() => setShowCategoryModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCategory ? "Editar Categoria" : "Nova Categoria"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCategorySubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nome da Categoria *</Form.Label>
                <Form.Control
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={categoryForm.description}
                  onChange={(e) =>
                    setCategoryForm({
                      ...categoryForm,
                      description: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ordem *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={categoryForm.order}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          order: parseInt(e.target.value),
                        })
                      }
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoria Pré-requisito</Form.Label>
                    <Form.Select
                      value={categoryForm.prerequisite}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          prerequisite: e.target.value,
                        })
                      }
                    >
                      <option value="">Nenhum pré-requisito</option>
                      {Array.isArray(categories) &&
                        categories
                          .filter((cat) =>
                            editingCategory
                              ? cat.id !== editingCategory.id
                              : true
                          )
                          .map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowCategoryModal(false)}
              >
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                {editingCategory ? "Atualizar" : "Criar"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal Vídeo */}
        <Modal
          show={showVideoModal}
          onHide={() => setShowVideoModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingVideo ? "Editar Vídeo" : "Novo Vídeo"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleVideoSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Título do Vídeo *</Form.Label>
                <Form.Control
                  type="text"
                  value={videoForm.title}
                  onChange={(e) =>
                    setVideoForm({ ...videoForm, title: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={videoForm.description}
                  onChange={(e) =>
                    setVideoForm({ ...videoForm, description: e.target.value })
                  }
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoria *</Form.Label>
                    <Form.Select
                      value={videoForm.category}
                      onChange={(e) =>
                        setVideoForm({ ...videoForm, category: e.target.value })
                      }
                      required
                    >
                      <option value="">Selecione uma categoria</option>
                      {Array.isArray(categories) &&
                        categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ordem *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={videoForm.order}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          order: parseInt(e.target.value),
                        })
                      }
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="is-active-check"
                      label="Vídeo ativo (visível para os usuários)"
                      checked={videoForm.is_active}
                      onChange={(e) =>
                        setVideoForm({
                          ...videoForm,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    <Form.Text className="text-muted">
                      Desmarque para deixar o vídeo inativo (não será exibido
                      para os usuários)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Seção de Tipo de Vídeo e Upload */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Tipo de Vídeo *</Form.Label>
                    <div className="d-flex gap-3">
                      <Form.Check
                        type="radio"
                        id="video-type-external"
                        name="video_type"
                        label="Vídeo Externo (YouTube/Vimeo)"
                        checked={videoForm.video_type === "external"}
                        onChange={() =>
                          setVideoForm({ ...videoForm, video_type: "external" })
                        }
                      />
                      {user?.is_staff && (
                        <>
                          <Form.Check
                            type="radio"
                            id="video-type-internal"
                            name="video_type"
                            label="Upload de Vídeo (S3)"
                            checked={videoForm.video_type === "internal" && !useLocalUpload}
                            onChange={() => {
                              setVideoForm({
                                ...videoForm,
                                video_type: "internal",
                              });
                              setUseLocalUpload(false);
                            }}
                          />
                          <Form.Check
                            type="radio"
                            id="video-type-local"
                            name="video_type"
                            label="Upload Local (Servidor) 🆕"
                            checked={videoForm.video_type === "internal" && useLocalUpload}
                            onChange={() => {
                              setVideoForm({
                                ...videoForm,
                                video_type: "internal",
                              });
                              setUseLocalUpload(true);
                            }}
                          />
                        </>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {videoForm.video_type === "external" ? (
                <Form.Group className="mb-3">
                  <Form.Label>URL do Vídeo (YouTube/Vimeo) *</Form.Label>
                  <Form.Control
                    type="url"
                    value={videoForm.external_url}
                    onChange={(e) =>
                      setVideoForm({
                        ...videoForm,
                        external_url: e.target.value,
                      })
                    }
                    placeholder="https://www.youtube.com/watch?v=..."
                    required={videoForm.video_type === "external"}
                  />
                </Form.Group>
              ) : (
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      Upload de Vídeo
                      {useLocalUpload && (
                        <Badge bg="success" className="ms-2">
                          📏 Local
                        </Badge>
                      )}
                    </h6>
                    {useLocalUpload && (
                      <small className="text-success">
                        ✅ Vídeo será salvo localmente no servidor (máx 500MB)
                      </small>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Arquivo de Vídeo *</Form.Label>
                      <Form.Control
                        type="file"
                        accept="video/*"
                        onChange={handleVideoFileChange}
                      />
                      <Form.Text className="text-muted">
                        Formatos: MP4, MOV, AVI, MKV, WebM. Máximo 500MB.
                      </Form.Text>
                      {videoFileError && (
                        <div className="text-danger small mt-1">
                          {videoFileError}
                        </div>
                      )}
                    </Form.Group>

                    {videoFile && (
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <i
                            className="las la-video"
                            style={{ fontSize: "24px" }}
                          ></i>
                        </div>
                        <div className="flex-grow-1">
                          <strong>Arquivo Selecionado</strong>
                          {useLocalUpload && (
                            <Badge bg="success" size="sm" className="ms-2">
                              Upload Local
                            </Badge>
                          )}
                          <br />
                          <small className="text-muted">
                            {videoFile.name} (
                            {Math.round(videoFile.size / (1024 * 1024))}MB)
                          </small>
                          <br />
                          
                          {/* Progress bar durante upload */}
                          {isUploading && (
                            <div className="mt-2 mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small><strong>Fazendo upload...</strong></small>
                                <small>{uploadProgress}%</small>
                              </div>
                              <div className="progress" style={{ height: "8px" }}>
                                <div 
                                  className="progress-bar progress-bar-striped progress-bar-animated bg-success" 
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {!isUploading && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="mt-1"
                              onClick={removeVideoFile}
                            >
                              Remover
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {!videoFile && videoForm.s3_url && (
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <i
                            className="las la-video"
                            style={{ fontSize: "24px" }}
                          ></i>
                        </div>
                        <div>
                          <strong>Vídeo Atual</strong>
                          <br />
                          <small className="text-muted">
                            Arquivo hospedado na S3
                          </small>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Duração</Form.Label>
                <Form.Control
                  type="time"
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  step="1"
                />
                <Form.Text className="text-muted">
                  Formato aceito: HH:MM:SS ou MM:SS (exemplos: 01:30:45 para 1h
                  30min 45s, ou 25:30 para 25min 30s)
                </Form.Text>
              </Form.Group>

              {/* Seção de Thumbnail */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Imagem de Thumbnail</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload de Imagem (Recomendado)</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                        />
                        <Form.Text className="text-muted">
                          Formatos: JPG, PNG, WebP. Máximo 1MB.
                        </Form.Text>
                        {thumbnailError && (
                          <div className="text-danger small mt-1">
                            {thumbnailError}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ou URL da Thumbnail</Form.Label>
                        <Form.Control
                          type="url"
                          value={videoForm.thumbnail_url}
                          onChange={(e) =>
                            setVideoForm({
                              ...videoForm,
                              thumbnail_url: e.target.value,
                            })
                          }
                          placeholder="https://exemplo.com/imagem.jpg"
                          disabled={!!thumbnailFile}
                        />
                        <Form.Text className="text-muted">
                          Desabilitado se você fez upload de imagem.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Preview da thumbnail */}
                  {thumbnailPreview && (
                    <Row className="mt-3">
                      <Col md={12}>
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <img
                              src={thumbnailPreview}
                              alt="Preview"
                              style={{
                                width: "120px",
                                height: "68px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "1px solid #dee2e6",
                              }}
                            />
                          </div>
                          <div>
                            <strong>Preview da Thumbnail</strong>
                            <br />
                            <small className="text-muted">
                              {thumbnailFile?.name} (
                              {Math.round(thumbnailFile?.size / 1024)}KB)
                            </small>
                            <br />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="mt-1"
                              onClick={removeThumbnail}
                            >
                              Remover
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}

                  {/* Preview da URL da thumbnail */}
                  {videoForm.thumbnail_final_url && !thumbnailFile && (
                    <Row className="mt-3">
                      <Col md={12}>
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <img
                              src={videoForm.thumbnail_final_url}
                              alt="Preview URL"
                              style={{
                                width: "120px",
                                height: "68px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "1px solid #dee2e6",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                          <div>
                            <strong>Preview da URL</strong>
                            <br />
                            <small className="text-muted">
                              {videoForm.thumbnail_final_url}
                            </small>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
              
              {/* Seção de Materiais Auxiliares (Links) */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Materiais Auxiliares</h6>
                </Card.Header>
                <Card.Body>
                  {/* Links existentes */}
                  {videoLinks.length > 0 && (
                    <div className="mb-3">
                      {videoLinks.map((link, index) => (
                        <div
                          key={link.id}
                          className="d-flex align-items-center justify-content-between p-2 mb-2"
                          style={{
                            background: "var(--gray-50, #f8fafc)",
                            borderRadius: "8px",
                            border: "1px solid var(--border-color, #e2e8f0)",
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>
                              {link.title}
                            </div>
                            {link.description && (
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                {link.description}
                              </div>
                            )}
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary"
                              style={{ fontSize: "0.75rem", wordBreak: "break-all" }}
                            >
                              {link.url}
                            </a>
                          </div>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className="ms-2 flex-shrink-0"
                            onClick={() => handleRemoveLink(link.id)}
                          >
                            Remover
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Formulário para adicionar novo link */}
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Titulo do material</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Ex: Slides da aula, Planilha de exercicios..."
                      value={newLink.title}
                      onChange={(e) =>
                        setNewLink({ ...newLink, title: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">URL do material</Form.Label>
                    <Form.Control
                      type="url"
                      size="sm"
                      placeholder="https://docs.google.com/... ou qualquer link"
                      value={newLink.url}
                      onChange={(e) =>
                        setNewLink({ ...newLink, url: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Descricao (opcional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Descreva brevemente o conteudo deste material..."
                      value={newLink.description}
                      onChange={(e) =>
                        setNewLink({ ...newLink, description: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddLink}
                    disabled={!newLink.title.trim() || !newLink.url.trim()}
                  >
                    + Adicionar material
                  </Button>
                  <Form.Text className="text-muted d-block mt-2">
                    Adicione links de documentos, planilhas, slides ou qualquer material complementar da aula.
                  </Form.Text>
                </Card.Body>
              </Card>

              {/* Informações sobre Upload Local */}
              {user?.is_staff && (
                <Alert variant="info" className="mb-0">
                  <div className="d-flex align-items-start">
                    <i className="las la-info-circle me-2 mt-1" style={{ fontSize: "18px" }}></i>
                    <div>
                      <strong>📏 Upload Local (Apenas Administradores)</strong>
                      <ul className="mb-0 mt-2 small">
                        <li><strong>Máximo:</strong> 500MB por arquivo</li>
                        <li><strong>Formatos:</strong> MP4, AVI, MOV, MKV, WebM</li>
                        <li><strong>Categoria:</strong> Deve pertencer ao conteúdo global (account 3)</li>
                        <li><strong>Segurança:</strong> Vídeos protegidos por autenticação e streaming</li>
                        <li><strong>Performance:</strong> Armazenamento local no servidor</li>
                      </ul>
                    </div>
                  </div>
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowVideoModal(false)}
                disabled={submittingVideo || isUploading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={submittingVideo || isUploading}
              >
                {submittingVideo || isUploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {isUploading 
                      ? `Fazendo upload... ${uploadProgress}%`
                      : editingVideo ? "Atualizando..." : "Criando..."
                    }
                  </>
                ) : editingVideo ? (
                  "Atualizar"
                ) : (
                  useLocalUpload ? "Criar e Fazer Upload" : "Criar"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </TrainingAccessControl>
  );
};

export default TrainingManagement;
